<template>
	<side-window class="fraud-alarm" :title="_ruleTitle" :tabs="tabs" :active="active" @pop="$emit('pop')" @close="$emit('close')" @tabSelect="tabSelect">
		<div v-if="alarm">

			<!-- Persistent header strip — severity / category / status are
			     properties of the alarm itself, so they belong outside the tabs. -->
			<div class="fraud-alarm__summary">
				<severity-badge-view :severity="alarm.severity" />
				<span class="fraud-alarm__category">{{ _category }}</span>
				<span class="fraud-alarm__status" :class="'fraud-alarm__status--' + alarm.status">● {{ _status }}</span>
			</div>

			<!-- Generelt: metadata → summary → rule explainer → evidence tags -->
			<form-grid-layout v-show="active === 'general'">
				<div>
					<read-view label="Vedrører" :value="_about"   />
					<read-view label="Aktør"    :value="_actor"   />
					<read-view label="Oprettet" :value="_created" />
				</div>
				<div>
					<block-view label="Resumé" :text="_summary" />
				</div>
				<div>
					<block-view label="Hvad denne alarm betyder" :text="_ruleDescription" />
				</div>
				<div>
					<div class="fraud-alarm__section-label">Evidens</div>
					<fraud-evidence-view :alarm="alarm" />
				</div>
			</form-grid-layout>

			<!-- Kommentar -->
			<div v-show="active === 'comments'" class="fraud-alarm__comments">
				<fraud-comments-view :comments="_comments" @send="addNote" />
			</div>
		</div>

		<div slot="footer">
			<button-view v-if="alarm && alarm.status !== 'dismissed'" label="Afvis"   @click="changeStatus('dismissed')" />
			<button-view v-if="alarm && alarm.status !== 'escalated'" label="Eskalér" @click="changeStatus('escalated')" />
			<button-view v-if="alarm && alarm.status !== 'open'"      label="Genåbn"  @click="changeStatus('open')"      />
		</div>
	</side-window>
</template>

<script>
import { bus } from '../../bus';
import { ruleTitle, ruleDescription } from '../fraud/rules';
import ReadView           from '../views/ReadView';
import BlockView          from '../views/BlockView';
import SeverityBadgeView  from '../views/SeverityBadgeView';
import FraudEvidenceView  from '../views/FraudEvidenceView';
import FraudCommentsView  from '../views/FraudCommentsView';

const STATUS_LABELS = {
	open:      'Åben',
	dismissed: 'Afvist',
	escalated: 'Eskaleret'
};

const STATUS_ACTION = {
	dismissed: 'dismiss',
	escalated: 'escalate',
	open:      'reopen'
};

export default {
	props: ['ent'],
	components: { ReadView, BlockView, SeverityBadgeView, FraudEvidenceView, FraudCommentsView },
	data() {
		return {
			alarm: null,
			active: 'general'
		};
	},
	computed: {
		tabs() {
			return [
				{ name: 'general',  label: 'Generelt'  },
				{ name: 'comments', label: 'Kommentar' }
			];
		},
		_ruleTitle()       { return this.alarm ? ruleTitle(this.alarm.rule) : 'Alarm'; },
		_ruleDescription() { return ruleDescription(this.alarm.rule); },
		_category()        { return { financial: 'Økonomi', leave: 'Fravær', hours: 'Timer' }[this.alarm.category] || this.alarm.category; },
		_status()          { return STATUS_LABELS[this.alarm.status] || this.alarm.status; },
		_summary()         { return (this.alarm.evidence && this.alarm.evidence.summary) || '—'; },
		_actor()           { return this.alarm.actor_name || '—'; },
		_about()           { return this.alarm.about_employee_name || '—'; },
		_created() {
			const d = new Date(this.alarm.created);
			return isNaN(d.getTime()) ? this.alarm.created : d.toLocaleString('da-DK');
		},
		_comments() { return (this.alarm._embedded && this.alarm._embedded.comments) || []; }
	},
	mounted() { this.alarm = this.ent; },
	methods: {
		tabSelect(name, tabName) { this.active = tabName; },

		async refetch() {
			const r = await this.axios.get('/fraud/alarms/' + this.alarm.id + '/');
			this.alarm = r.data;
		},

		async changeStatus(newStatus) {
			if (!this.alarm || newStatus === this.alarm.status) return;

			const kind = STATUS_ACTION[newStatus];
			if (!kind) return;

			let note = '';
			if (kind !== 'reopen') {
				note = window.prompt('Begrundelse (valgfri, men anbefales for ' + (kind === 'escalate' ? 'eskalering' : 'afvisning') + '):') || '';
			}

			try {
				await this.axios.put('/fraud/alarms/' + this.alarm.id + '/' + kind + '/', { note: note || undefined });
				await this.refetch();
				bus.$emit('update');
			} catch (err) {
				alert('Fejlede: ' + ((err.response && err.response.data && err.response.data.error) || err.message));
			}
		},

		async addNote(body) {
			try {
				await this.axios.post('/fraud/alarms/' + this.alarm.id + '/comments/', { body });
				await this.refetch();
			} catch (err) {
				alert('Fejlede: ' + ((err.response && err.response.data && err.response.data.error) || err.message));
			}
		}
	}
};
</script>

<style lang="scss" scoped>
.fraud-alarm {
	&__summary {
		display: flex;
		gap: 12px;
		align-items: center;
		padding: 16px 20px;
		border-bottom: 1px solid #2d2f36;
	}

	&__category {
		font-size: 12px;
		color: #a0a3ac;
		text-transform: uppercase;
		letter-spacing: 0.5px;
	}

	&__status {
		margin-left: auto;
		font-size: 12px;
		letter-spacing: 0.3px;

		&--open      { color: #f7223f; }
		&--dismissed { color: #888;    }
		&--escalated { color: #d6a000; }
	}

	&__section-label {
		font-size: 11px;
		color: #888;
		text-transform: uppercase;
		letter-spacing: 0.5px;
		margin-bottom: 10px;
	}

	&__comments {
		padding: 20px;
	}
}
</style>
